import { trackAnalyticsEvent, EVENT_NAME } from '@leagueplatform/analytics';
import { warnMissingAnalyticsFields } from './warn-missing-analytics-fields';
import {
  Detail2UpCardWithCTAButtonClick,
  DetailCarouselHeaderButtonClick,
  DetailCarouselHeaderSelectItem,
  DetailCarouselScrollLeft,
  DetailCarouselScrollRight,
  DetailHeroBannerButtonClick,
  DetailLargeVisualCardButtonClick,
  DetailProgressBarButtonClick,
  DetailProgressRingButtonClick,
  DetailSmallVisualCardButtonClick,
  DetailStackSelectItem,
  DetailTextWithCTAButtonClick,
  DetailXSVisualCardButtonClick,
  DetailNotificationBannerButtonClick,
  DetailNotificationCardButtonClick,
  DetailMetricCardButtonClick,
  DetailNotificationBannerDismissClick,
} from '../constants/analytics-event-detail-mapping';
import { WidgetType } from '../types/widget-types';

const getDataForAnalyticsByWidgetType = (
  type: WidgetType,
  widgetAnalyticsData: any,
) => {
  switch (type) {
    case WidgetType.XS_VISUAL_CARD:
      return {
        text_detail: widgetAnalyticsData.heading,
        text_sub_detail: widgetAnalyticsData.body,
        link_url: widgetAnalyticsData.linkUrl,
      };
    case WidgetType.PROGRESS_BAR:
      return {
        text_detail: widgetAnalyticsData.eyebrow,
        current_progress: widgetAnalyticsData.currentText,
        maximum_progress: widgetAnalyticsData.suffixText,
        remaining_progress: widgetAnalyticsData.footerText,
        progress_percent: widgetAnalyticsData.progress,
        link_url: widgetAnalyticsData.action,
      };
    case WidgetType.PROGRESS_RING:
      return {
        text_detail: widgetAnalyticsData.eyebrow,
        current_progress: widgetAnalyticsData.currentText,
        maximum_progress: widgetAnalyticsData.suffixText,
        progress_percent: widgetAnalyticsData.progress,
        link_url: widgetAnalyticsData.action,
      };
    case WidgetType.HERO_BANNER:
      return {
        text_detail: widgetAnalyticsData.buttonLabel,
        link_url: widgetAnalyticsData.buttonAction,
      };

    case WidgetType.NOTIFICATION_CARD:
      return {
        text_detail: widgetAnalyticsData.heading,
        text_sub_detail: widgetAnalyticsData.subheading,
        link_url: widgetAnalyticsData?.url,
      };
    case WidgetType.METRIC_CARD:
      return {
        text_detail: widgetAnalyticsData.heading,
      };
    default:
      return {
        text_detail: widgetAnalyticsData.title,
        text_sub_detail: widgetAnalyticsData.description,
        link_url: widgetAnalyticsData.linkUrl,
      };
  }
};

/**
 * Function @getAnalyticsEventsForMasonryWSDriver generate callbacks events
 *  based on the @widgetType from its arguments.
 * @param widgetType - type of the widget eg, text_with_large_image
 * @param widgetAnalyticsData - Data response of the widget from get_widget_data api call
 * @param appId - App Id/container name of the Masonry screen
 * @param productArea - Product area
 * @param screenName - Screen name
 * @param productIdentifier - Product identifier in response of the widget from get_widget_data api call
 * @returns
 */

// Duplicate of widgetAnalyticsEvents inside modules/masonry/utils
export const getAnalyticsEventsForMasonryWSDriver = (
  widgetType: WidgetType,
  widgetAnalyticsData: any,
  appId: string,
  productArea: string,
  screenName: string,
  productIdentifier: string,
) => {
  warnMissingAnalyticsFields(`Widget "${widgetType}"`, {
    product_area: productArea,
    sub_product_area: appId,
    screen_name: screenName,
  });

  /**
   * func trackMasonryAnalytics
   * @param {EVENT_NAME} event: The name of the event being tracked.
   * @param {object} widgetProperties: analytics properties that are unique to the widget event.
   * @returns A function that calls trackAnalyticsEvent with common analytics properties used across default masonry widgets.
   */
  const trackMasonryAnalytics =
    (event: EVENT_NAME, widgetProperties: object) => () =>
      trackAnalyticsEvent(event, {
        product_area: productArea,
        sub_product_area: appId,
        screen_name: screenName,
        product_identifier: productIdentifier,
        ...widgetProperties,
      });

  const getCarouselAnalyticEvents = () => {
    const carouselRollup = {
      carousel_array: widgetAnalyticsData?.items?.map(
        (carouselItem: any) => carouselItem.title,
      ),
      carousel_name: widgetAnalyticsData?.title,
      carousel_num_resources: widgetAnalyticsData?.items?.length,
    };
    return {
      onScrollLeft: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselScrollLeft,
        ...carouselRollup,
      }),
      onScrollRight: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselScrollRight,
        ...carouselRollup,
      }),
      onSelectItem: (item: any, index: number) =>
        trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
          detail: DetailCarouselHeaderSelectItem,
          sub_detail: item.title,
          text_detail: item.overline,
          carousel_index: index,
          link_url: item.linkUrl,
          ...carouselRollup,
        })(),
      onSelectHeader: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselHeaderButtonClick,
        sub_detail: widgetAnalyticsData?.headerLinkHint,
        link_url: widgetAnalyticsData?.headerLinkUrl,
        ...carouselRollup,
      }),
    };
  };

  const getTwoUpCardAnalyticsEvent = () => ({
    onItemClick: ({ heading, body, actionLink }: any, index: number) =>
      trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: Detail2UpCardWithCTAButtonClick,
        text_detail: heading,
        text_sub_detail: body,
        container_index: index,
        link_url: actionLink,
      })(),
  });

  const getNotificationBannerAnalyticsEvent = () => ({
    // Returning onCardClick callback only when there is a url present for the widget
    ...(widgetAnalyticsData?.url && {
      onCardClick: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailNotificationBannerButtonClick,
        text_detail: widgetAnalyticsData?.heading,
        text_sub_detail: widgetAnalyticsData?.body,
        link_url: widgetAnalyticsData?.url,
      }),
    }),
    onDismiss: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
      detail: DetailNotificationBannerDismissClick,
      text_detail: widgetAnalyticsData?.heading,
      text_sub_detail: widgetAnalyticsData?.body,
      link_url: widgetAnalyticsData?.url,
    }),
  });

  const getStackAnalyticsEvents = () => {
    const stackRollup = {
      stack_array: widgetAnalyticsData.items.map(
        (stackItem: any) =>
          stackItem?.attributes?.title || stackItem?.attributes?.heading,
      ),
      stack_name: widgetAnalyticsData.heading,
      stack_num_resources: widgetAnalyticsData.items.length,
    };
    return {
      onItemClick: ({ attributes, type }: any, index: number) =>
        trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
          detail: DetailStackSelectItem,
          stack_index: index,
          component_detail: type,
          ...getDataForAnalyticsByWidgetType(type as WidgetType, attributes),
          ...stackRollup,
        })(),
    };
  };

  const getGenericCarouselAnalyticsEvents = () => {
    const carouselRollup = {
      carousel_array: widgetAnalyticsData.items.map(
        (stackItem: any) =>
          stackItem?.attributes?.title || stackItem?.attributes?.heading,
      ),
      carousel_name: widgetAnalyticsData.heading,
      carousel_num_resources: widgetAnalyticsData.items.length,
    };
    return {
      onScrollLeft: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselScrollLeft,
        ...carouselRollup,
      }),
      onScrollRight: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselScrollRight,
        ...carouselRollup,
      }),
      onItemClick: ({ attributes, type }: any, index: number) =>
        trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
          detail: DetailCarouselHeaderSelectItem,
          carousel_index: index,
          component_detail: type,
          ...getDataForAnalyticsByWidgetType(type as WidgetType, attributes),
          ...carouselRollup,
        })(),
      onSelectHeader: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
        detail: DetailCarouselHeaderButtonClick,
        sub_detail: widgetAnalyticsData?.headerButtonLabel,
        link_url: widgetAnalyticsData?.headerButtonAction,
        ...carouselRollup,
      }),
    };
  };

  // Use this function when there are common details to be passed to the analytics
  // In case of passing additional properties, create a separate function and
  // add a mapping to the widget type
  // - Pass widget detail to this function
  // - This function returns a callback function which can be used to pass widget attributes
  const getCommonAnalyticsEvent = (widgetDetail: string) => () => ({
    onCardClick: trackMasonryAnalytics(EVENT_NAME.BUTTON_CLICKED, {
      detail: widgetDetail,
      ...getDataForAnalyticsByWidgetType(widgetType, widgetAnalyticsData),
    }),
  });

  // Update this map when a new default widget is added to Masonry
  const getWidgetAnalyticsFromType: {
    [widgetType: string]: () => {
      [analyticEvents: string]: (...args: any[]) => void;
    };
  } = {
    /* ---- Complex Widgets ----*/
    [WidgetType.CAROUSEL]: getCarouselAnalyticEvents,
    [WidgetType.TWO_UP_CARD]: getTwoUpCardAnalyticsEvent,
    [WidgetType.STACK]: getStackAnalyticsEvents,
    [WidgetType.GENERIC_CAROUSEL]: getGenericCarouselAnalyticsEvents,
    /* ---- Simple Widgets ----*/
    [WidgetType.HERO_BANNER]: getCommonAnalyticsEvent(
      DetailHeroBannerButtonClick,
    ),
    [WidgetType.PROGRESS_BAR]: getCommonAnalyticsEvent(
      DetailProgressBarButtonClick,
    ),
    [WidgetType.PROGRESS_RING]: getCommonAnalyticsEvent(
      DetailProgressRingButtonClick,
    ),
    [WidgetType.XS_VISUAL_CARD]: getCommonAnalyticsEvent(
      DetailXSVisualCardButtonClick,
    ),
    [WidgetType.NOTIFICATION_BANNER]: getNotificationBannerAnalyticsEvent,
    [WidgetType.NOTIFICATION_CARD]: getCommonAnalyticsEvent(
      DetailNotificationCardButtonClick,
    ),
    [WidgetType.TEXT_WITH_CTA_CARD]: getCommonAnalyticsEvent(
      DetailTextWithCTAButtonClick,
    ),
    [WidgetType.LARGE_VISUAL_CARD]: getCommonAnalyticsEvent(
      DetailLargeVisualCardButtonClick,
    ),
    [WidgetType.SMALL_VISUAL_CARD]: getCommonAnalyticsEvent(
      DetailSmallVisualCardButtonClick,
    ),
    [WidgetType.METRIC_CARD]: getCommonAnalyticsEvent(
      DetailMetricCardButtonClick,
    ),
  };

  const widgetAnalyticsEventsFn = getWidgetAnalyticsFromType[widgetType];

  return widgetAnalyticsEventsFn ? widgetAnalyticsEventsFn() : {};
};
